import {
  Activity,
  BarChart3,
  CalendarClock,
  Building2,
  CheckSquare,
  GraduationCap,
  History, // GAVION my-calls
  KanbanSquare,
  ListChecks,
  LayoutDashboard,
  Mail,
  MessageSquareQuote,
  Mic,
  Phone,
  PhoneCall,
  Settings,
  ShieldCheck,
  Sparkles,
  Tags,
  Target,
  Wallet,
  BadgePercent,
  Package,
  Users,
  UserSearch, // GAVION recruiting
  Zap,
  type LucideIcon,
  Plug,
} from "lucide-react";

export type NavItem = {
  key: string;
  to: string;
  icon: LucideIcon;
  labelKey: string;
  mobileLabelKey?: string;
  accent?: boolean;
  mobile: "tab" | "more" | "none";
  enabled: boolean;
  matchPrefixes: readonly string[];
  /** GAVION ai-email: a live count next to the label. */
  badge?: "ai_drafts" | "meetings_unmatched" | "job_applications_new";
  /** GAVION recruiting: shown to admins only (the route is RequireAdmin too). */
  adminOnly?: boolean;
  /** GAVION my-calls: links listed under the item (sidebar sub-menu; on mobile, in the « Plus » sheet). */
  children?: readonly NavSubItem[];
};
export type NavSubItem = {
  key: string;
  to: string;
  icon: LucideIcon;
  labelKey: string;
};

// Spec 10 §4. `enabled: false` = the page ships in a later batch (5: courriels, 6: réunions), which flips it.
export const NAV_ITEMS: readonly NavItem[] = [
  {
    key: "dashboard",
    to: "/",
    icon: LayoutDashboard,
    labelKey: "gavion.nav.dashboard",
    mobileLabelKey: "gavion.nav.home",
    mobile: "tab",
    enabled: true,
    matchPrefixes: [],
  },
  {
    key: "call",
    to: "/appeler",
    icon: Phone,
    labelKey: "gavion.nav.call",
    accent: true,
    mobile: "tab",
    enabled: true,
    matchPrefixes: ["/appeler"],
    // GAVION my-calls: « Mes appels » under « Appeler »
    children: [
      {
        key: "my_calls",
        to: "/appeler/historique",
        icon: History,
        labelKey: "gavion.nav.my_calls",
      },
    ],
  },
  {
    key: "leads",
    to: "/companies",
    icon: Building2,
    labelKey: "gavion.nav.leads",
    mobile: "tab",
    enabled: true,
    matchPrefixes: ["/companies"],
  },
  {
    key: "contacts",
    to: "/contacts",
    icon: Users,
    labelKey: "gavion.nav.contacts",
    mobile: "more",
    enabled: true,
    matchPrefixes: ["/contacts"],
  },
  {
    key: "deals",
    to: "/deals",
    icon: KanbanSquare,
    labelKey: "gavion.nav.deals",
    mobile: "more",
    enabled: true,
    matchPrefixes: ["/deals"],
  },
  {
    key: "tasks",
    to: "/tasks",
    icon: CheckSquare,
    labelKey: "gavion.nav.tasks",
    mobile: "tab",
    enabled: true,
    matchPrefixes: ["/tasks"],
  },
  {
    key: "meetings",
    to: "/reunions",
    icon: Mic,
    labelKey: "gavion.nav.meetings",
    mobile: "more",
    enabled: true, // GAVION batch 6
    matchPrefixes: ["/reunions"],
    badge: "meetings_unmatched", // GAVION granola partners: « À associer » count
  },
  {
    key: "email",
    to: "/courriels",
    icon: Mail,
    labelKey: "gavion.nav.email",
    mobile: "more",
    enabled: true, // GAVION batch 5 (B5-T9): email section shipped
    matchPrefixes: ["/courriels"],
  },
  // GAVION ai-email: proposed AI drafts to approve (count badge)
  {
    key: "drafts",
    to: "/brouillons",
    icon: Sparkles,
    labelKey: "gavion.nav.drafts",
    mobile: "more",
    enabled: true,
    matchPrefixes: ["/brouillons"],
    badge: "ai_drafts",
  },
  {
    key: "reports",
    to: "/rapports",
    icon: BarChart3,
    labelKey: "gavion.nav.reports",
    mobile: "more",
    enabled: true,
    matchPrefixes: ["/rapports"],
  },
  // GAVION recruiting: job postings and candidates (admins only; count of new applications)
  {
    key: "recruiting",
    to: "/recrutement",
    icon: UserSearch,
    labelKey: "gavion.nav.recruiting",
    mobile: "more",
    enabled: true,
    matchPrefixes: ["/recrutement"],
    badge: "job_applications_new",
    adminOnly: true,
  },
  // GAVION training: the gamified dialer course, for every user
  {
    key: "training",
    to: "/formation",
    icon: GraduationCap,
    labelKey: "gavion.nav.training",
    mobile: "more",
    enabled: true,
    matchPrefixes: ["/formation"],
  },
  {
    key: "settings",
    to: "/parametres",
    icon: Settings,
    labelKey: "gavion.nav.settings",
    mobile: "more",
    enabled: true,
    matchPrefixes: ["/parametres", "/settings", "/profile"],
  },
];

/** GAVION recruiting: admin-only items (« Recrutement ») are left out unless isAdmin. */
export const visibleNavItems = (isAdmin = false) =>
  NAV_ITEMS.filter((i) => i.enabled && (isAdmin || !i.adminOnly));
export const mobileTabs = () =>
  visibleNavItems().filter((i) => i.mobile === "tab");
export const mobileMoreItems = (isAdmin = false) =>
  visibleNavItems(isAdmin).filter((i) => i.mobile === "more");
/** GAVION my-calls: the sub-links of the mobile tabs, which have no sub-menu there (shown in the « Plus » sheet). */
export const mobileTabSubItems = (): NavSubItem[] =>
  mobileTabs().flatMap((i) => i.children ?? []);
/** GAVION my-calls: the sub-link that is the current page, if any. */
export const activeNavSubKey = (pathname: string): string | null =>
  NAV_ITEMS.flatMap((i) => i.children ?? []).find(
    (c) => pathname === c.to || pathname.startsWith(`${c.to}/`),
  )?.key ?? null;
export const activeNavKey = (pathname: string): string | null => {
  if (pathname === "/" || pathname === "") return "dashboard";
  return (
    NAV_ITEMS.find((i) =>
      i.matchPrefixes.some(
        (p) => pathname === p || pathname.startsWith(`${p}/`),
      ),
    )?.key ?? null
  );
};

export type SettingsLink = {
  to: string;
  icon: LucideIcon;
  labelKey: string;
  adminOnly: boolean;
  /** GAVION import duplicates: a live count next to the label. */
  badge?: "data_quality_duplicates";
};

// Batch 2 Paramètres pages (spec 04, 05). Reps get a read-only automations view; RequireAdmin guards the rest.
export const SETTINGS_LINKS: readonly SettingsLink[] = [
  {
    to: "/parametres/telephone",
    icon: Phone,
    labelKey: "gavion.softphone.title",
    adminOnly: false,
  },
  {
    to: "/parametres/automatisations",
    icon: Zap,
    labelKey: "gavion.automation.title",
    adminOnly: false,
  },
  // GAVION X2: objection library (reps read, admins edit; the page gates itself)
  {
    to: "/parametres/objections",
    icon: MessageSquareQuote,
    labelKey: "gavion.objections.title",
    adminOnly: false,
  },
  // GAVION custom pipeline: the deal stages (admins)
  {
    to: "/parametres/pipeline",
    icon: KanbanSquare,
    labelKey: "gavion.pipeline.title",
    adminOnly: true,
  },
  // GAVION lead statuses: the lead statuses (admins)
  {
    to: "/parametres/statuts-leads",
    icon: Tags,
    labelKey: "gavion.lead_statuses.title",
    adminOnly: true,
  },
  {
    to: "/parametres/appels",
    icon: PhoneCall,
    labelKey: "gavion.call_settings.title",
    adminOnly: true,
  },
  // GAVION open-pixel: Paramètres → Courriels (open tracking)
  {
    to: "/parametres/courriels",
    icon: Mail,
    labelKey: "gavion.email_settings.title",
    adminOnly: true,
  },
  // GAVION X1: daily goals
  {
    to: "/parametres/objectifs",
    icon: Target,
    labelKey: "gavion.goals.settings.title",
    adminOnly: true,
  },
  // GAVION rep stats: compensation and fixed costs (acquisition cost)
  {
    to: "/parametres/couts",
    icon: Wallet,
    labelKey: "gavion.costs.settings.title",
    adminOnly: true,
  },
  // GAVION commissions: the package catalog and the commission plans (admins)
  {
    to: "/parametres/forfaits",
    icon: Package,
    labelKey: "gavion.commissions_settings.packages.title",
    adminOnly: true,
  },
  {
    to: "/parametres/plans-commission",
    icon: BadgePercent,
    labelKey: "gavion.commissions_settings.plans.title",
    adminOnly: true,
  },
  // GAVION X3: data quality (duplicates, fix queue)
  {
    to: "/parametres/qualite",
    icon: ListChecks,
    labelKey: "gavion.data_quality.title",
    adminOnly: true,
    badge: "data_quality_duplicates", // GAVION import duplicates: the pairs waiting there
  },
  {
    to: "/parametres/sante",
    icon: Activity,
    labelKey: "gavion.health.title",
    adminOnly: true,
  },
  // GAVION: batch 7: Law 25 tools and audit log
  {
    to: "/parametres/confidentialite",
    icon: ShieldCheck,
    labelKey: "gavion.privacy.title",
    adminOnly: true,
  },
  // GAVION batch 3: Microsoft connect and signature are for every user
  {
    to: "/parametres/integrations",
    icon: Plug,
    labelKey: "gavion.nav.integrations",
    adminOnly: false,
  },
  // GAVION ai-email: Paramètres → IA (« Mon style » for everyone; the admin parts gate themselves)
  {
    to: "/parametres/ia",
    icon: Sparkles,
    labelKey: "gavion.ai.settings.title",
    adminOnly: false,
  },
  // GAVION booking: every user's own demo booking link (Paramètres → Mon calendrier de démo)
  {
    to: "/parametres/calendrier-demo",
    icon: CalendarClock,
    labelKey: "gavion.booking.settings.title",
    adminOnly: false,
  },
];

export const settingsLinks = (isAdmin: boolean) =>
  SETTINGS_LINKS.filter((l) => isAdmin || !l.adminOnly);
